import { useEffect, useRef, useState } from "react";
import { Box, Paper, Typography, Button, Collapse, IconButton, Alert } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useUser } from "../../context/userContext";
import { getAgentsByGroupId, getAllAgentGroups, getAllAgents } from "../../api/userApi";
import { getPricing } from "../../api/resourceApi";
import { createSales, checkDuplicateSale } from "../../api/salesApi";
import { convertDateStringToDateTime } from '../../utils/datetimeUtils';
import { useNavigate, useLocation } from "react-router-dom";
import GenericSalesForm from "../common/genericSalesForm";
import SaleFormDetails from "./saleFormDetails";
import { buildFrozenPricing } from "../../utils/frozenPricing";
import { methodBreakdown, termForMethod, PAYMENT_METHOD_OPTIONS } from "../../utils/ccPricing";
import { UseFetchAgentGroupData } from "../../hooks/useFetchAgent";

const PP = "'Poppins', sans-serif";

function todayDDMMYYYY() {
    const d = new Date();
    const p = n => String(n).padStart(2, '0');
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
}

// Full PDF "Form Details" snapshot template so the section renders the SAME
// fields as an RSC/CSC-created sale (SaleFormDetails only shows keys that exist
// in the payload). Seeded empty; the user fills them on the create form.
function defaultFormPayload(category) {
    const common = {
        salutation: 'MR.',
        name: '', icNumber: '', phone: '', email: '',
        tinNumber: '', address: '', tnbAccountNo: '',
        date: todayDDMMYYYY(),
        emergencyName: '', emergencyRelationship: '', emergencyIc: '',
        emergencyPhone: '', emergencyAddress: '', emergencyEmail: '',
        operationWarrantyYears: '1 year',
        insuranceAmount: 10000,
        depositPaymentMethod: '',
        paymentMethodLabel: '',
        quotationPaymentPlan: 36,
    };
    if (String(category).toUpperCase() === 'COMMERCIAL') {
        return {
            ...common,
            companyName: '', companyTin: '', companyOffice: '', companyFax: '', companyEmail: '', companyWebsite: '',
            picSalutation: 'MR.', picName: '', picMykad: '', picPosition: '', picCitizenship: '', picMobile: '', picEmail: '',
            altSalutation: 'MR.', altName: '', altMykad: '', altPosition: '', altRelationship: '', altMobile: '', altEmail: '',
        };
    }
    return common;
}

export default function CreateSalesForm() {
    const navigate = useNavigate();

    const { user } = useUser();
    const userId = user?.userId;

    const [successMsg, setSuccessMsg] = useState("");
    // const [loading, setLoading] = useState(true);
    // const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // ===== Agent Details States =====
    const { agentGroup, agentNameOptions, loading: agentLoading } = UseFetchAgentGroupData(userId);
    const [selectedAgentId, setSelectedAgentId] = useState(userId);
    const [coBrokeGroupOptions, setCoBrokeGroupOptions] = useState([]);
    const [coBrokeNameOptions, setCoBrokeNameOptions] = useState([]);
    const [selectedCoBrokeGroupId, setSelectedCoBrokeGroupId] = useState();
    const [selectedCoBrokeAgentId, setSelectedCoBrokeAgentId] = useState();
    const [referralOptions, setReferralOptions] = useState([]);
    const [selectedReferralId, setSelectedReferralId] = useState('');

    // ===== Customer Details States =====
    const [customerName, setCustomerName] = useState("");
    const [customerState, setCustomerState] = useState("");
    const [customerCity, setCustomerCity] = useState("");
    const [customerPostcode, setCustomerPostcode] = useState("");
    const [customerEmail, setCustomerEmail] = useState("");
    const [customerPhone, setCustomerPhone] = useState("");
    const [customerAddress, setCustomerAddress] = useState("");

    // ===== Category and Product States =====
    const [selectedCategory, setSelectedCategory] = useState("RESIDENTIAL");
    const [productList, setProductList] = useState([]);

    // ===== Panel and System Size States =====
    const [panelQty, setPanelQty] = useState(0);
    const [energy, setEnergy] = useState(0);
    // RSC hand-off seeds the panel-wattage dropdown (UI-only). Null → form default 640W.
    const [rscPanelWatt, setRscPanelWatt] = useState(null);

    // ===== Payment Method States =====
    // Shared canonical list — IDENTICAL to the RSC calculator dropdown.
    const availablePaymentMethods = PAYMENT_METHOD_OPTIONS;
    const [paymentMethod, setPaymentMethod] = useState("CASH");

    // ===== Price States =====
    const [originalPrice, setOriginalPrice] = useState(0);
    const [canEditPrice, setCanEditPrice] = useState(false);

    const [addOnPrice, setAddOnPrice] = useState(0);

    // ===== Residential Matching State =====
    const [residentialMatch, setResidentialMatch] = useState(null);

    // RSC-style sizing verdict (inverter / ratio / SEDA-CCC) from the form — gates submit.
    const [sizing, setSizing] = useState(null);

    // ===== Phase Mode (Residential only) =====
    // Drives which ATAP pricing column getPackagePrice picks: cashPrice/cC36Price/cC60Price (single)
    // vs cash3p8k/cC36_3p8k/cC60_3p8k (3-phase). Default single — 3-phase is the explicit override.
    const [phaseMode, setPhaseMode] = useState('single');

    // ===== Pricing Calculation States =====
    const [finalPrice, setFinalPrice] = useState();
    const [asternDiscount, setAsternDiscount] = useState();
    const [agentDiscount, setAgentDiscount] = useState();
    const [depositOn, setDepositOn] = useState();

    // ===== Discount Funding States =====
    const [discountFundedBy, setDiscountFundedBy] = useState('Own');
    const [agentDiscountContribution, setAgentDiscountContribution] = useState(0);
    const [uplineDiscountContribution, setUplineDiscountContribution] = useState(0);

    // ===== Add-on Line Items =====
    const [lineItems, setLineItems] = useState([]);

    // ===== RSC-style promos (auto-fill Astern discount) — default BLANK =====
    // Promos must NOT be pre-selected: the auto-fill only fires when the agent
    // deliberately ticks one, so the Astern discount is never silently set behind
    // their back. See feedback_price_freeze_no_retroactive_atap.
    // Default-ticked on create (auto-fills the Astern discount); Per-Battery stays off
    // (only applies once a 16 kWh battery is added).
    const [promoBackupbox, setPromoBackupbox] = useState(true);
    const [promoFirst640, setPromoFirst640] = useState(true);
    const [promoPerBatt, setPromoPerBatt] = useState(false);

    // ===== Form Details (for PDFs) — same section as the sale page =====
    const [formDetailsOpen, setFormDetailsOpen] = useState(false);
    const [formPayload, setFormPayload] = useState(() => defaultFormPayload('RESIDENTIAL'));

    // Re-seed the snapshot keys when the category changes (residential <-> commercial),
    // preserving any values the user already typed for keys that still apply.
    useEffect(() => {
        setFormPayload(prev => {
            const base = defaultFormPayload(selectedCategory);
            const merged = { ...base };
            if (prev) for (const k of Object.keys(base)) if (prev[k] !== undefined && prev[k] !== '') merged[k] = prev[k];
            return merged;
        });
    }, [selectedCategory]);

    // RSC hand-off: when the user clicks "Create Sale" in the RSC calculator, it
    // navigates here with state.rscPrefill — we map its data into this form so EVERY
    // sale goes through this one consistent page (RSC mechanism unchanged). One-shot.
    const location = useLocation();
    const rscPrefillRef = useRef(false);
    // RSC folds the inverter-upgrade cost into its system price, so the prefilled
    // originalPrice can differ from the live package list. Keep it pinned (against the
    // auto-price effect below) until the agent changes the kWp away from the prefilled
    // value — at which point normal auto-fill resumes.
    const prefillPriceRef = useRef(null);
    const prefillEnergyRef = useRef(null);
    useEffect(() => {
        if (rscPrefillRef.current) return;
        const pre = location.state?.rscPrefill;
        if (!pre) return;
        rscPrefillRef.current = true;
        if (pre.selectedCategory) setSelectedCategory(pre.selectedCategory);
        if (pre.phaseMode) setPhaseMode(pre.phaseMode);
        if (pre.panelQty != null) setPanelQty(pre.panelQty);
        if (pre.energy != null) setEnergy(pre.energy);
        if (pre.panelWatt != null) setRscPanelWatt(pre.panelWatt);
        if (pre.paymentMethod) setPaymentMethod(pre.paymentMethod);
        if (pre.originalPrice != null) {
            setOriginalPrice(pre.originalPrice); setCanEditPrice(true);
            prefillPriceRef.current = pre.originalPrice;
            prefillEnergyRef.current = Number(pre.energy);
        }
        if (pre.asternDiscount != null) setAsternDiscount(pre.asternDiscount);
        if (pre.agentDiscount != null) setAgentDiscount(pre.agentDiscount);
        if (pre.addOnPrice != null) setAddOnPrice(pre.addOnPrice);
        if (Array.isArray(pre.lineItems)) setLineItems(pre.lineItems);
        if (pre.promoBackupbox != null) setPromoBackupbox(!!pre.promoBackupbox);
        if (pre.promoFirst640 != null) setPromoFirst640(!!pre.promoFirst640);
        if (pre.promoPerBatt != null) setPromoPerBatt(!!pre.promoPerBatt);
        if (pre.customerName) setCustomerName(pre.customerName);
        if (pre.customerPhone) setCustomerPhone(pre.customerPhone);
        if (pre.customerEmail) setCustomerEmail(pre.customerEmail);
        if (pre.customerAddress) setCustomerAddress(pre.customerAddress);
        if (pre.customerState) setCustomerState(pre.customerState);
        if (pre.customerCity) setCustomerCity(pre.customerCity);
        if (pre.customerPostcode) setCustomerPostcode(pre.customerPostcode);
        if (pre.selectedAgentId) setSelectedAgentId(pre.selectedAgentId);
        if (pre.selectedCoBrokeGroupId) setSelectedCoBrokeGroupId(pre.selectedCoBrokeGroupId);
        if (pre.selectedCoBrokeAgentId) setSelectedCoBrokeAgentId(pre.selectedCoBrokeAgentId);
        if (pre.formPayload) setFormPayload(prev => ({ ...(prev || {}), ...pre.formPayload }));
    }, [location.state]);

    const handleFormFieldChange = (key, value) => {
        setFormPayload(prev => ({ ...(prev || {}), [key]: value }));
    };

    // ===== Possible-duplicate warning (WARN-only, never blocks submit) =====
    const [dupMatches, setDupMatches] = useState([]);
    const dupTimerRef = useRef(null);
    const customerIc = formPayload?.icNumber || '';
    useEffect(() => {
        if (dupTimerRef.current) clearTimeout(dupTimerRef.current);
        const name = (customerName || '').trim();
        const phone = (customerPhone || '').trim();
        const ic = (customerIc || '').trim();
        if (!name && !phone && !ic) { setDupMatches([]); return; }
        dupTimerRef.current = setTimeout(() => {
            checkDuplicateSale({ name, phone, ic })
                .then(r => setDupMatches(Array.isArray(r.data) ? r.data : []))
                .catch(() => setDupMatches([]));
        }, 500);
        return () => { if (dupTimerRef.current) clearTimeout(dupTimerRef.current); };
    }, [customerName, customerPhone, customerIc]);

    // Combined one-line address (same format the RSC builder + sale page use) so
    // the PDF snapshot address matches the split Customer-section fields.
    const combinedAddress = [
        customerAddress,
        [customerPostcode, customerCity].filter(s => s && String(s).trim()).join(' '),
        customerState,
    ].filter(s => s && String(s).trim()).join(', ').toUpperCase();

    useEffect(() => {
        if (userId) {
            setSelectedAgentId(userId);
        }
    }, [userId]);

    useEffect(() => {
        const fetchAgentGroups = async () => {
            try {
                const response = await getAllAgentGroups();
                if (response.status === 200) {
                    setCoBrokeGroupOptions(response.data);
                }
                else {
                    console.error("Unexpected response:", response);
                }
            }
            catch (error) {
                console.error("Error fetching Agent groups:", error);
            }
        }
        fetchAgentGroups();
    }, []);

    // Load Referral-role users for the optional Referral picker.
    useEffect(() => {
        getAllAgents().then(r => {
            const refs = (r.data || []).filter(u =>
                u.status === 'Active' && (u.userRoles || []).some(ur => ur.roleName === 'Referral')
            );
            setReferralOptions(refs);
        }).catch(err => console.error("Error fetching referral users:", err));
    }, []);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const response = await getPricing();
                if (response.status === 200) {
                    // Sales form only needs the Residential Pricing list (ATAP Pricing page source)
                    setProductList((response.data || []).filter(r => r.listName === 'Residential Pricing'));
                }
                else {
                    console.error("Unexpected response:", response);
                }
            }
            catch (error) {
                console.error("Error fetching pricing:", error);
            }
        }
        fetchProducts();
    }, []);

    // Map sales form paymentMethod values to ResourcePricing field names.
    // Honors phaseMode: 'single' uses cashPrice/cC36Price/cC60Price, '3phase' uses cash3p8k/etc.
    // Falls back to the other phase if the chosen column is null/0 (rare edge case).
    // Original Price is the CASH base (Section 1) — always the cash column,
    // regardless of payment method. The Payment Method section converts the whole
    // thing to the chosen basis (cash ÷ factor) for saving / quotation / commission.
    const getPackagePrice = (match, _method, phase = 'single') => {
        return phase === '3phase'
            ? (Number(match.cash3p8k) || Number(match.cashPrice) || 0)
            : (Number(match.cashPrice) || Number(match.cash3p8k) || 0);
    };

    // // ===== Calculate Energy (kWp) =====
    // useEffect(() => {
    //     if (panelQty > 0) {
    //         const calculatedSize = Number((panelQty * 0.62).toFixed(2));
    //         setEnergy(calculatedSize);
    //     }
    //     else {
    //         setEnergy(0);
    //     }
    // }, [panelQty]);

    // ===== Set Price Editing Permission Based on Category =====
    useEffect(() => {
        // Both Residential and Commercial can edit price
        setCanEditPrice(true);

        // Reset price when switching categories
        setOriginalPrice(0);
        setResidentialMatch(null);
    }, [selectedCategory]);

    // ===== RESIDENTIAL LOGIC: Auto-populate Price =====
    // Match by kWp only (admin sets pricing by kWp, not by panel count × wattage).
    // If no match, agent can still enter price manually — submission is not blocked.
    useEffect(() => {
        if (selectedCategory === "RESIDENTIAL" && energy > 0) {
            const match = productList.find(p => Number(p.energy) === Number(energy));
            setResidentialMatch(match);

            // Re-assert an RSC-prefilled price while the kWp is unchanged (it folds in
            // the inverter upgrade, which the live package list doesn't). We actively
            // SET it — not just "keep current" — because the category-reset effect and
            // mount ordering can blank originalPrice after the prefill ran. Once the
            // agent changes the kWp, drop the pin and resume normal auto-fill.
            if (prefillPriceRef.current != null && Number(energy) === prefillEnergyRef.current) {
                setOriginalPrice(prefillPriceRef.current);
                return;
            }
            prefillPriceRef.current = null;

            if (match && paymentMethod) {
                setOriginalPrice(getPackagePrice(match, paymentMethod, phaseMode));
            }
            else if (!match) {
                setOriginalPrice(0);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCategory, panelQty, energy, paymentMethod, productList, phaseMode]);

    // ===== Calculate Net Price =====
    useEffect(() => {
        const oriPrice = originalPrice || 0;
        const asternDisc = asternDiscount || 0;
        const agentDisc = agentDiscount || 0;
        const afterDisc = oriPrice - asternDisc - agentDisc;

        setFinalPrice(Math.max(0, afterDisc));

    }, [originalPrice, asternDiscount, agentDiscount]);

    const handleAgentGroupChange = async (event) => {
        const selectedCoBrokeGroupId = event.target.value;
        console.log('[cobroke] picked groupId:', selectedCoBrokeGroupId, 'currentUserId:', user.userId);
        setSelectedCoBrokeGroupId(selectedCoBrokeGroupId);
        setSelectedCoBrokeAgentId("");

        if (selectedCoBrokeGroupId) {
            try {
                const response = await getAgentsByGroupId(selectedCoBrokeGroupId);
                console.log('[cobroke] API returned:', response.status, 'rows:', response.data?.length, response.data);
                const filtered = (response.data || []).filter(agent => String(agent.userId) !== String(user.userId));
                console.log('[cobroke] after filter:', filtered.length, filtered);
                setCoBrokeNameOptions(filtered);
            }
            catch (error) {
                console.error('[cobroke] API error:', error.response?.status, error.response?.data, error.message);
                setCoBrokeNameOptions([]);
            }
        }
        else {
            setCoBrokeNameOptions([]);
        }
    };

    const handleCancel = (event) => {
        event.preventDefault();
        navigate("/sales");
    }

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (submitting) {
            return;
        }

        if (!customerName || !customerPhone || !customerAddress || !customerState || !customerCity || !paymentMethod) {
            alert("Please fill in all required fields.");
            return;
        }

        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (customerEmail) {
            if (!emailRegex.test(customerEmail)) {
                alert("Please enter a valid email address.");
                return;
            }
        }

        // ===== Validate panel quantity =====
        if (panelQty <= 0) {
            alert("Please enter a valid number of panels.");
            return;
        }

        // ===== RSC-style sizing gate — only a valid config can be created =====
        // (over-SEDA needs a CCC add-on; the safety ratio must be within cap).
        if (sizing && !sizing.valid) {
            alert(sizing.reason || "This configuration is not valid. Fix the inverter/SEDA/CCC issue before creating the sale.");
            return;
        }

        // No residential match: allow manual price entry (originalPrice validation below catches RM 0).

        const phoneDigits = customerPhone.replace(/\D/g, '');
        if (phoneDigits.length < 10 || phoneDigits.length > 12) {
            alert("Phone number must be between 10 and 12 digits.");
            return;
        }

        if (!originalPrice || originalPrice <= 0) {
            alert("Original price must be greater than 0.");
            return;
        }

        // Convert the CASH base (Section 1) to the chosen payment-method basis
        // (Section 2) — this is what's SAVED and what commission + quotation run on.
        // Cash = unchanged; CC = cash ÷ factor (prices round-up RM10, discounts
        // rounded). New sales only — existing sales are never recomputed.
        // Shared converter (RSC parity): system uses the CC price list; the TOTAL rounds
        // up the whole cash final ONCE; add-on = residual. Keeps save = display = quotation.
        const _bd = methodBreakdown({ originalPrice, asternDiscount, agentDiscount, addOnPrice }, termForMethod(paymentMethod));
        const sOriginal = _bd.original;
        const sAstern = _bd.astern;
        const sAgent = _bd.agent;
        const sAddon = _bd.addon;
        const sFinal = _bd.final;

        const newSalesInfo = {
            "agentId": selectedAgentId,
            "coAgentId": selectedCoBrokeAgentId || null,
            "referralUserId": selectedReferralId || null,
            "discountByAstern": sAstern || null,
            "discountByAgent": sAgent || null,
            "panelQty": panelQty,
            "energy": energy,
            "originalPrice": sOriginal,
            "finalPrice": sFinal,
            "addOnPrice": sAddon || null,
            "lineItems": lineItems.length > 0 ? lineItems : null,
            "productCategory": selectedCategory,
            "paymentMethod": paymentMethod,
            "phase": phaseMode === '3phase' ? '3phase' : 'single',
            "depositOn": convertDateStringToDateTime(depositOn) || null,
            // PDF/form snapshot so generated forms match (mirrors the sale page).
            // Customer name/phone/email/address are the single source of truth.
            // Pricing is FROZEN here from the matched ATAP row as of now — later ATAP
            // price changes never alter this sale's quotation (only an edit does).
            "formPayloadJson": JSON.stringify({
                ...(formPayload || {}),
                ...buildFrozenPricing({
                    match: residentialMatch,
                    is3p: phaseMode === '3phase',
                    energy, panelQty, originalPrice,
                    asternDiscount, agentDiscount, addOnPrice, lineItems,
                    promoBackupbox, promoFirst640, promoPerBatt,
                    paymentMethod,
                }),
                name: customerName.toUpperCase(),
                phone: customerPhone || '',
                email: (customerEmail || '').toLowerCase(),
                address: combinedAddress,
            }),
            "createdBy": user.username,
            "discountFundedBy": agentDiscount > 0 ? discountFundedBy : null,
            "agentDiscountContribution": agentDiscount > 0 ? agentDiscountContribution : null,
            "uplineDiscountContribution": agentDiscount > 0 ? uplineDiscountContribution : null,
            "createCustomerRequest": {
                "customerName": customerName.toUpperCase(),
                "email": (customerEmail || '').toLowerCase(),
                "phoneNumber": customerPhone,
                "address": (customerAddress || '').toUpperCase(),
                "state": customerState || null,
                "city": customerCity || null,
                "postcode": customerPostcode || null
            }
        };

        setSubmitting(true);

        try {
            const response = await createSales(newSalesInfo);
            if (response.status === 201) {
                setSuccessMsg("Sales created successfully!");
                // Open the new sale's page so they can add docs / signature next.
                const newId = response.data?.salesId;
                setTimeout(() => {
                    navigate(newId ? `/accounting/sales/${newId}` : "/sales");
                }, 1000);

                return;
            }
            else {
                alert("Sales creation failed. Please check again on the details.");
                console.error("Unexpected response:", response);
            }
        }
        catch (error) {
            if (error.response?.status === 401) {
                alert("Session expired. Please log in again.");
                navigate('/login');

                return;
            }
            // Surface backend validation messages (e.g. referrer rate exceeds agent PSC%).
            // Backend returns BadRequest with { message } payload — fall back to a string body
            // when present, then to the generic notice if neither is available.
            const data = error.response?.data;
            const serverMsg = (data && (data.message || data.title))
                || (typeof data === 'string' ? data : '');
            alert(serverMsg || "Sales creation errored. Please check again on the details.");
            console.error("Error creating sales:", error.response?.data || error);
        }
        setSubmitting(false);
    }

    // if (agentLoading) {
    //     return <div>Loading...</div>;
    // }

    // if (error) {
    //     return <div className="alert alert-danger">{error}</div>;
    // }

    return (
        <Box sx={{ width: '100%' }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}
                sx={{ mb: 2, fontFamily: PP, textTransform: 'none', color: '#64748B', fontWeight: 500, fontSize: '0.85rem' }}>
                Back
            </Button>

            <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: PP, color: '#0F172A', mb: 0.5 }}>
                Create New Sale
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', color: '#64748B', fontFamily: PP, mb: 2 }}>
                Fill in the details below to register a new sale
            </Typography>

            {successMsg && (
                <Box sx={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: '8px', p: 1.5, color: '#065F46', fontSize: '0.85rem', mb: 2, fontFamily: PP }}>
                    {successMsg}
                </Box>
            )}

            <form onSubmit={handleSubmit}>
            {/* Two-column layout mirrors the sale page: left = form + form details,
                right = sticky Create action card. */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.5fr) minmax(380px, 1fr)' },
                gap: 2,
                alignItems: 'start',
            }}>
                {/* Left column */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <Paper elevation={0} sx={{ p: 3, borderRadius: '14px', border: '1px solid #E2E8F0' }}>
                    <GenericSalesForm
                        mode="create"
                        agentGroup={agentGroup}
                        agentNameOptions={agentNameOptions}
                        selectedAgentId={selectedAgentId}
                        setSelectedAgentId={setSelectedAgentId}
                        coBrokeGroupOptions={coBrokeGroupOptions}
                        coBrokeNameOptions={coBrokeNameOptions}
                        selectedCoBrokeGroupId={selectedCoBrokeGroupId}
                        selectedCoBrokeAgentId={selectedCoBrokeAgentId}
                        setSelectedCoBrokeAgentId={setSelectedCoBrokeAgentId}
                        referralOptions={referralOptions}
                        selectedReferralId={selectedReferralId}
                        setSelectedReferralId={setSelectedReferralId}
                        customerName={customerName}
                        setCustomerName={setCustomerName}
                        customerEmail={customerEmail}
                        setCustomerEmail={setCustomerEmail}
                        customerPhone={customerPhone}
                        setCustomerPhone={setCustomerPhone}
                        customerAddress={customerAddress}
                        setCustomerAddress={setCustomerAddress}
                        customerState={customerState}
                        setCustomerState={setCustomerState}
                        customerCity={customerCity}
                        setCustomerCity={setCustomerCity}
                        customerPostcode={customerPostcode}
                        setCustomerPostcode={setCustomerPostcode}
                        selectedCategory={selectedCategory}
                        setSelectedCategory={setSelectedCategory}
                        phaseMode={phaseMode}
                        setPhaseMode={setPhaseMode}
                        panelQty={panelQty}
                        setPanelQty={setPanelQty}
                        energy={energy}
                        setEnergy={setEnergy}
                        initialPanelWattage={rscPanelWatt}
                        originalPrice={originalPrice}
                        setOriginalPrice={setOriginalPrice}
                        availablePaymentMethods={availablePaymentMethods}
                        paymentMethod={paymentMethod}
                        setPaymentMethod={setPaymentMethod}
                        canEditPrice={canEditPrice}
                        residentialMatch={residentialMatch}
                        onSizingChange={setSizing}
                        finalPrice={finalPrice}
                        addOnPrice={addOnPrice}
                        setAddOnPrice={setAddOnPrice}
                        depositOn={depositOn}
                        setDepositOn={setDepositOn}
                        asternDiscount={asternDiscount}
                        setAsternDiscount={setAsternDiscount}
                        agentDiscount={agentDiscount}
                        setAgentDiscount={setAgentDiscount}
                        discountFundedBy={discountFundedBy}
                        setDiscountFundedBy={setDiscountFundedBy}
                        agentDiscountContribution={agentDiscountContribution}
                        setAgentDiscountContribution={setAgentDiscountContribution}
                        uplineDiscountContribution={uplineDiscountContribution}
                        setUplineDiscountContribution={setUplineDiscountContribution}
                        handleAgentGroupChange={handleAgentGroupChange}
                        lineItems={lineItems}
                        setLineItems={setLineItems}
                        canEditAddons={true}
                        showPromos={true}
                        promoBackupbox={promoBackupbox} setPromoBackupbox={setPromoBackupbox}
                        promoFirst640={promoFirst640} setPromoFirst640={setPromoFirst640}
                        promoPerBatt={promoPerBatt} setPromoPerBatt={setPromoPerBatt}
                    />
                </Paper>

                {/* Form Details (for PDFs) — same collapsible section as the sale page */}
                <Paper elevation={0} sx={{ p: 3, borderRadius: '14px', border: '1px solid #E2E8F0' }}>
                    <Box
                        onClick={() => setFormDetailsOpen(o => !o)}
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                    >
                        <Box>
                            <Typography sx={{ fontWeight: 700, fontFamily: PP, color: '#0F172A', fontSize: '0.95rem' }}>
                                Form Details (for PDFs)
                            </Typography>
                            <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', fontFamily: PP }}>
                                Customer / document info used only by the generated forms — not part of the sale.
                            </Typography>
                        </Box>
                        <IconButton size="small" sx={{ color: '#64748B' }}>
                            {formDetailsOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                    </Box>
                    <Collapse in={formDetailsOpen} unmountOnExit>
                        <Box sx={{ mt: 2 }}>
                            <SaleFormDetails
                                payload={formPayload}
                                onChange={handleFormFieldChange}
                                canEdit={true}
                                lockedFields={{
                                    name: (customerName || '').toUpperCase(),
                                    phone: customerPhone || '',
                                    email: (customerEmail || '').toLowerCase(),
                                    address: combinedAddress,
                                }}
                            />
                        </Box>
                    </Collapse>
                </Paper>
                </Box>

                {/* Right column: sticky Create action card (mirrors the sale page's Save box) */}
                <Box sx={{ position: 'sticky', top: 16, alignSelf: 'start' }}>
                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #E2E8F0' }}>
                        <Typography sx={{ fontWeight: 700, fontFamily: PP, color: '#0F172A', fontSize: '0.95rem', mb: 1.5 }}>
                            New Sale
                        </Typography>
                        {dupMatches.length > 0 && (
                            <Alert severity="warning" sx={{ mb: 1.5, fontFamily: PP, '& .MuiAlert-message': { fontFamily: PP, fontSize: '0.78rem' } }}>
                                <Typography sx={{ fontWeight: 700, fontFamily: PP, fontSize: '0.8rem', mb: 0.5 }}>
                                    Possible duplicate — {dupMatches.length} existing sale{dupMatches.length === 1 ? '' : 's'} match this customer:
                                </Typography>
                                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                    {dupMatches.map(m => (
                                        <li key={m.salesId}>
                                            #{m.salesId} — {m.customerName || '—'}
                                            {m.agentName ? ` · ${m.agentName}` : ''}
                                            {m.status ? ` · ${m.status}` : ''}
                                            {m.depositOn ? ` · ${new Date(m.depositOn).toLocaleDateString()}` : ''}
                                        </li>
                                    ))}
                                </Box>
                            </Alert>
                        )}
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={submitting}
                            sx={{
                                fontFamily: PP, textTransform: 'none', fontWeight: 600, fontSize: '0.9rem',
                                borderRadius: '10px', py: 1.1, boxShadow: 'none',
                                background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)',
                            }}
                        >
                            {submitting ? 'Creating…' : 'Create Sale'}
                        </Button>
                        <Button
                            type="button"
                            fullWidth
                            variant="outlined"
                            onClick={handleCancel}
                            sx={{
                                mt: 1, fontFamily: PP, textTransform: 'none', fontWeight: 600, fontSize: '0.85rem',
                                borderRadius: '10px', borderColor: '#CBD5E1', color: '#64748B',
                            }}
                        >
                            Cancel
                        </Button>
                        <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', fontFamily: PP, mt: 1.5 }}>
                            After creating, you'll be taken to the sale page to upload documents and request the signature.
                        </Typography>
                    </Paper>
                </Box>
            </Box>
            </form>
        </Box>
    );
}
